import { SessionResponse } from '@aeris/contracts'

/**
 * The last session the server confirmed, kept so the shell can render offline.
 *
 * Without this the offline application is useless in a way that looks like a
 * bug: the service worker serves the shell, `/auth/me` cannot be reached,
 * `session` is null, and the operator lands on a login screen that by
 * definition cannot authenticate anybody. Caching the identity is what turns
 * "the page loads" into "the till is usable".
 *
 * ## What this is not
 *
 * It is **not** authentication. The session cookie remains the only credential
 * and the API re-checks it on every request, so a forged entry here buys
 * access to nothing — the moment the network returns, every call is judged by
 * the server. What it buys is a name in the corner and a sidebar with the
 * right items on it while there is no server to ask.
 *
 * ## Why it expires
 *
 * A terminal that is taken offline would otherwise show the last operator's
 * identity and permissions forever. The window is one shift: long enough that a
 * connection dropping mid-day is invisible, short enough that a machine found
 * disconnected the next morning has forgotten who used it.
 *
 * Signing out clears it, and so does a 401 — the server saying "not you" is an
 * answer, and an answer must not be survived by a cache.
 */

const KEY = 'aeris.session-cache'

/** One shift. See the note above on why this expires at all. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000

type Cached = {
  savedAt: number
  session: unknown
}

export const rememberSession = (session: SessionResponse): void => {
  try {
    /*
     * The bearer token is stripped, always.
     *
     * `SessionResponse` carries one only when a client asked for bearer
     * transport, which the browser never does — it uses the httpOnly cookie
     * (ADR-0012), and the whole point of that choice is that no script on the
     * page can reach the credential. Writing a token here would undo it for
     * everybody the day some other caller sets that field, so this does not
     * depend on the current transport being the safe one.
     */
    const { token: _discarded, ...withoutCredential } = session
    const entry: Cached = { savedAt: Date.now(), session: withoutCredential }
    window.localStorage.setItem(KEY, JSON.stringify(entry))
  } catch {
    // A full or blocked storage costs the offline shell, not the application.
  }
}

export const forgetSession = (): void => {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // Nothing to do, and nothing worth telling the operator.
  }
}

/**
 * The cached session, or null when there is none, it expired, or it no longer
 * matches the contract.
 *
 * Re-parsing through `SessionResponse` rather than trusting the stored shape is
 * the point: `localStorage` survives deployments, so a build from before a
 * contract change can hand this one a session that no longer typechecks. A
 * stale entry must fail as "no cached session", never as a render crash on a
 * screen whose whole job is to work when nothing else does.
 */
export const recallSession = (): SessionResponse | null => {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(KEY)
  } catch {
    return null
  }
  if (raw === null) return null

  try {
    // Parsed as `unknown` on purpose: `JSON.parse` will happily return `null`
    // or a number, and asserting the shape here would make the guard below
    // look redundant to the type checker while still being the thing that
    // stops a bad entry.
    const entry: unknown = JSON.parse(raw)
    if (
      typeof entry !== 'object' ||
      entry === null ||
      typeof (entry as Cached).savedAt !== 'number' ||
      Date.now() - (entry as Cached).savedAt > MAX_AGE_MS
    ) {
      forgetSession()
      return null
    }

    const parsed = SessionResponse.safeParse((entry as Cached).session)
    if (!parsed.success) {
      forgetSession()
      return null
    }
    return parsed.data
  } catch {
    forgetSession()
    return null
  }
}
